/**
 * Color Schemes for Kalamly
 * Each scheme defines a set of CSS custom properties
 */

export const COLOR_SCHEMES = {
  default: {
    name: 'Default Blue',
    emoji: '🔵',
    light: {
      '--accent': '#3b82f6',
      '--accent-hover': '#2563eb',
      '--accent-light': 'rgba(59, 130, 246, 0.1)',
    },
    dark: {
      '--accent': '#60a5fa',
      '--accent-hover': '#3b82f6',
      '--accent-light': 'rgba(96, 165, 250, 0.1)',
    }
  },
  green: {
    name: 'Forest Green',
    emoji: '🌲',
    light: {
      '--accent': '#10b981',
      '--accent-hover': '#059669',
      '--accent-light': 'rgba(16, 185, 129, 0.1)',
    },
    dark: {
      '--accent': '#34d399',
      '--accent-hover': '#10b981',
      '--accent-light': 'rgba(52, 211, 153, 0.1)',
    }
  },
  purple: {
    name: 'Royal Purple',
    emoji: '💜',
    light: {
      '--accent': '#8b5cf6',
      '--accent-hover': '#7c3aed',
      '--accent-light': 'rgba(139, 92, 246, 0.1)',
    },
    dark: {
      '--accent': '#a78bfa',
      '--accent-hover': '#8b5cf6',
      '--accent-light': 'rgba(167, 139, 250, 0.1)',
    }
  },
  orange: {
    name: 'Sunset Orange',
    emoji: '🧡',
    light: {
      '--accent': '#f59e0b',
      '--accent-hover': '#d97706',
      '--accent-light': 'rgba(245, 158, 11, 0.1)',
    },
    dark: {
      '--accent': '#fbbf24',
      '--accent-hover': '#f59e0b',
      '--accent-light': 'rgba(251, 191, 36, 0.1)',
    }
  },
  pink: {
    name: 'Cherry Blossom',
    emoji: '🌸',
    light: {
      '--accent': '#ec4899',
      '--accent-hover': '#db2777',
      '--accent-light': 'rgba(236, 72, 153, 0.1)',
    },
    dark: {
      '--accent': '#f472b6',
      '--accent-hover': '#ec4899',
      '--accent-light': 'rgba(244, 114, 182, 0.1)',
    }
  },
  teal: {
    name: 'Ocean Teal',
    emoji: '🌊',
    light: {
      '--accent': '#14b8a6',
      '--accent-hover': '#0d9488',
      '--accent-light': 'rgba(20, 184, 166, 0.1)',
    },
    dark: {
      '--accent': '#2dd4bf',
      '--accent-hover': '#14b8a6',
      '--accent-light': 'rgba(45, 212, 191, 0.1)',
    }
  },
  red: {
    name: 'Crimson Red',
    emoji: '❤️',
    light: {
      '--accent': '#ef4444',
      '--accent-hover': '#dc2626',
      '--accent-light': 'rgba(239, 68, 68, 0.1)',
    },
    dark: {
      '--accent': '#f87171',
      '--accent-hover': '#ef4444',
      '--accent-light': 'rgba(248, 113, 113, 0.1)',
    }
  },
  indigo: {
    name: 'Deep Indigo',
    emoji: '🔮',
    light: {
      '--accent': '#6366f1',
      '--accent-hover': '#4f46e5',
      '--accent-light': 'rgba(99, 102, 241, 0.1)',
    },
    dark: {
      '--accent': '#818cf8',
      '--accent-hover': '#6366f1',
      '--accent-light': 'rgba(129, 140, 248, 0.1)',
    }
  }
};

/**
 * Apply a color scheme to the document
 * @param {string} schemeId - The ID of the color scheme to apply
 * @param {string} theme - Current theme ('light' or 'dark')
 */
export const applyColorScheme = (schemeId, theme) => {
  const scheme = COLOR_SCHEMES[schemeId];
  if (!scheme) return;

  const colors = theme === 'dark' ? scheme.dark : scheme.light;
  const root = document.documentElement;

  Object.entries(colors).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

/**
 * Get list of available color schemes
 * @returns {array} - Array of {id, name, emoji} objects
 */
export const getColorSchemes = () => {
  return Object.entries(COLOR_SCHEMES).map(([id, scheme]) => ({
    id,
    name: scheme.name,
    emoji: scheme.emoji
  }));
};
